import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { OrderBonusStatus } from './Common/Constants';
import { RouteParams } from 'app/models/Orders/Order';
import { XGrid } from '@AMIEWEB/Common';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { missingField } from 'app/constants';
import { orderBonusGridColumns } from './OrderBonusGridUtils';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { selectOrderBonusTab } from '@AMIEWEB/Order/Store/Order.selectors';
import { useParams } from 'react-router-dom';
import { usePromiseTracker } from 'react-promise-tracker';
import { useTranslation } from 'react-i18next';
import { OrderBonusHistory } from './OrderBonusHistory/OrderBonusHistory';
import { UseTemplate } from './CustomComponents/UseTemplate';
import { OrderBonusModal } from './SaveOrderBonus/OrderBonusModal';
import { CopyOrderBonusModal } from './CopyOrderBonus/CopyOrderBonusModal';

export const OrderBonusWrapper = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams<RouteParams>();
  const orderId = parseInt(params?.id);
  const defaultSortColumn = { field: 'statusChangeDate', sort: 'desc' };
  const apiRef = useGridApiRef();
  const [rows, setRows] = useState<any>([]);
  const [columns, setColumns] = useState<any>(orderBonusGridColumns());
  const [sortModel, setSortModel] = useState<any>([defaultSortColumn]);
  const [createButton, setCreateButton] = useState<{
    disabled: boolean;
    clicked: boolean;
    confirmationPopUp: boolean;
    useExistingTemplate: boolean;
    selectedData: any;
  }>({
    disabled: false,
    clicked: false,
    confirmationPopUp: false,
    useExistingTemplate: false,
    selectedData: null,
  });
  const [copyButton, setCopyButton] = useState<{ disabled: boolean; clicked: boolean; selectedBonusId: number }>({
    disabled: true,
    clicked: false,
    selectedBonusId: null,
  });
  const [deleteButton, setDeleteButton] = useState<{
    disabled: boolean;
    rowSelection: boolean;
    clicked: boolean;
    confirmationPopUp: boolean;
  }>({
    disabled: true,
    rowSelection: false,
    clicked: false,
    confirmationPopUp: false,
  });
  const [deleteRecords, setDeleteRecords] = useState<{
    pendingStatusRowsSelected: number[];
    totalRowsSelected: number;
  }>({
    pendingStatusRowsSelected: [],
    totalRowsSelected: null,
  });
  const [editButton, setEditButton] = useState<{ clicked: boolean; selectedData: any }>({
    clicked: false,
    selectedData: null,
  });
  const { promiseInProgress: isCallingOrderBonusListAPI } = usePromiseTracker({
    area: 'get-order-bonus-list',
    delay: 0,
  });
  const { promiseInProgress: isCallingChangeHistoryAPI } = usePromiseTracker({
    area: 'get-order-bonus-change-history',
    delay: 0,
  });
  const { promiseInProgress: isCallingDeleteOrderBonusAPI } = usePromiseTracker({
    area: 'delete-order-bonus',
    delay: 0,
  });
  const orderBonusTab = useSelector(selectOrderBonusTab);
  const orderBonusList = orderBonusTab?.orderBonusList;
  const orderBonusChangeHistory = orderBonusTab?.changeHistoryModal;

  const handleCreateOrderBonusModal = () => {
    setCreateButton({
      ...createButton,
      clicked: createButton?.selectedData ? false : true,
      confirmationPopUp: createButton?.selectedData ? true : false,
    });
  };

  const handleCopyOrderBonusModal = () => {
    setCopyButton({
      ...copyButton,
      clicked: true,
    });
  };

  const handleDeleteOrderBonusModal = () => {
    setDeleteButton({
      ...deleteButton,
      clicked: true,
      confirmationPopUp: true,
    });
  };

  const handleRowSelection = rowSelection => {
    setCreateButton(
      rowSelection.length === 1
        ? { ...createButton, disabled: false, selectedData: rowSelection.map(id => rows.find(row => row.id === id))[0] }
        : { ...createButton, disabled: rowSelection.length > 0 ? true : false, selectedData: null },
    );
    setCopyButton({
      disabled: rowSelection.length === 1 ? false : true,
      clicked: false,
      selectedBonusId:
        rowSelection.length === 1 ? Number(rowSelection.map(id => rows.find(row => row.id === id))[0].bonusId) : null,
    });
    setDeleteButton({
      ...deleteButton,
      rowSelection: rowSelection.length === 0 ? false : true,
      disabled:
        rowSelection
          .map(id => rows.filter(row => row.id === id && row.descStatus !== OrderBonusStatus.pending))
          .filter(validRow => validRow.length > 0).length > 0 || rowSelection.length === 0
          ? true
          : false,
    });
    setDeleteRecords({
      pendingStatusRowsSelected:
        rowSelection.length > 0
          ? rowSelection
              .map(id => rows.filter(row => row.id === id && row.descStatus === OrderBonusStatus.pending))
              .filter(validRow => validRow.length > 0)
              .map(x => x[0].bonusId)
          : [],
      totalRowsSelected: rowSelection.length > 0 ? rowSelection.length : null,
    });
  };

  const handleDeleteAction = () => {
    dispatch(
      orderDataActions.deleteOrderBonus({
        orderId: orderId,
        bonusIds: deleteRecords?.pendingStatusRowsSelected,
        totalRows: deleteRecords?.totalRowsSelected,
      }),
    );
    setDeleteButton({
      ...deleteButton,
      clicked: false,
      confirmationPopUp: false,
    });
  };

  const handleDeleteNoAction = () => {
    setDeleteButton({
      ...deleteButton,
      clicked: false,
      confirmationPopUp: false,
    });
  };

  const handleYesAction = () => {
    setCreateButton({
      ...createButton,
      clicked: true,
      confirmationPopUp: false,
      useExistingTemplate: true,
    });
  };

  const handleNoAction = () => {
    setCreateButton({
      ...createButton,
      clicked: true,
      confirmationPopUp: false,
      useExistingTemplate: false,
    });
    dispatch(orderDataActions.setOrderBonusShifts([]));
  };

  const handleCancelAction = () => {
    setCreateButton({
      ...createButton,
      clicked: false,
      confirmationPopUp: false,
    });
    setEditButton({
      ...editButton,
      clicked: false,
    });
  };

  useEffect(() => {
    if (orderId) {
      dispatch(orderDataActions.getOrderBonusList(orderId));
    }
  }, [dispatch, orderId]);

  useEffect(() => {
    if (orderBonusTab?.editBonusModal?.isEdit && orderBonusTab?.editBonusModal?.rowData !== null) {
      setEditButton({
        clicked: orderBonusTab?.editBonusModal?.isEdit,
        selectedData: orderBonusTab?.editBonusModal?.rowData,
      });
    }
  }, [orderBonusTab?.editBonusModal]);

  useEffect(() => {
    if (orderBonusTab?.isOrderBonusDeleted) {
      setDeleteButton({
        disabled: true,
        rowSelection: false,
        clicked: false,
        confirmationPopUp: false,
      });
      setDeleteRecords({
        pendingStatusRowsSelected: [],
        totalRowsSelected: null,
      });
    }
    if (orderBonusTab?.isOrderBonusCopied) {
      dispatch(orderDataActions.setCopyOrderBonus(false));
    }
    if (orderBonusTab?.isOrderBonusDeleted || orderBonusTab?.isOrderBonusCopied) {
      setRows([]);
      dispatch(orderDataActions.getOrderBonusList(orderId));
      setSortModel([defaultSortColumn]);
      setCopyButton({
        ...copyButton,
        clicked: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBonusTab?.isOrderBonusDeleted, orderBonusTab?.isOrderBonusCopied]);

  useEffect(() => {
    if (orderBonusList !== null) {
      const rowData = orderBonusList?.map((item, index) => ({
        ...item,
        bonusType: item?.descBonusType ?? missingField,
        bonusAmount: item?.bonusAmount ?? 0,
        bonusStartDate: item?.startDate ?? missingField,
        bonusEndDate: item?.endDate ?? missingField,
        bonusPayer: item?.descPayer ?? missingField,
        bonusStatus: item?.descStatus ?? missingField,
        statusChangeDate: item?.statusChangedDate ?? missingField,
        statusChangedBy:
          item?.changedByFirstName && item?.changedByLastName
            ? item?.changedByFirstName + ' ' + item?.changedByLastName
            : missingField,
        bonusId: item?.bonusId ?? missingField,
        bonusBillAmount: item?.bonusBillingAmount ?? 0,
        id: index + 1,
      }));
      setRows(rowData);
    }
  }, [orderBonusList]);
  const rightActions = useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-orderBonus-key',
        Icon: AddIcon,
        onClick: handleCreateOrderBonusModal,
        disabled: createButton?.disabled,
        tooltipProps: {
          tooltipContent: createButton?.disabled
            ? t('order.orderBonusTab.rightActions.addIconDisableMessage')
            : t('order.orderBonusTab.rightActions.addIconMessage'),
        },
      },
      {
        key: 'copy-orderBonus-key',
        Icon: ContentCopyOutlinedIcon,
        onClick: handleCopyOrderBonusModal,
        disabled: copyButton?.disabled,
        tooltipProps: {
          tooltipContent: copyButton?.disabled
            ? t('order.orderBonusTab.rightActions.copyIconDisableMessage')
            : t('order.orderBonusTab.rightActions.copyIconMessage'),
        },
      },
      {
        key: 'delete-orderBonus-key',
        Icon: DeleteOutlinedIcon,
        onClick: handleDeleteOrderBonusModal,
        disabled: deleteButton?.disabled,
        tooltipProps: {
          tooltipContent:
            deleteButton?.disabled && !deleteButton?.rowSelection
              ? t('order.orderBonusTab.rightActions.noRowSelectionDisableMessage')
              : deleteButton?.disabled && deleteButton?.rowSelection
              ? t('order.orderBonusTab.rightActions.deleteIconDisableMessage')
              : t('order.orderBonusTab.rightActions.deleteIconMessage'),
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createButton?.disabled, copyButton?.disabled, deleteButton?.disabled, deleteButton?.rowSelection]);

  return (
    <div style={{ height: 420 }}>
      <XGrid
        title={t('order.orderBonusTab.grid.title')}
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        checkboxSelection
        disableSelectionOnClick
        loading={isCallingOrderBonusListAPI || isCallingChangeHistoryAPI || isCallingDeleteOrderBonusAPI}
        height={380}
        pageSize={50}
        rowsPerPageOptions={[50]}
        sortModel={sortModel}
        onSortModelChange={model => setSortModel(model)}
        onSelectionModelChange={rowSelection => {
          handleRowSelection(rowSelection);
        }}
        toolbarProps={{
          rightActions: rightActions,
          refreshButton: true,
          resetButton: true,
          filterButton: true,
          refreshGrid: () => {
            setRows([]);
            dispatch(orderDataActions.getOrderBonusList(orderId));
            setSortModel([defaultSortColumn]);
          },
          resetColumns: () => {
            setColumns(orderBonusGridColumns());
          },
        }}
      />
      {(createButton?.confirmationPopUp || deleteButton?.confirmationPopUp) && (
        <UseTemplate
          openDialog={createButton?.confirmationPopUp || deleteButton?.confirmationPopUp}
          title={
            createButton?.confirmationPopUp
              ? t('order.orderBonusTab.confirmationTemplate.title')
              : t('order.orderBonusTab.confirmationTemplate.deleteTitle')
          }
          content={
            createButton?.confirmationPopUp
              ? t('order.orderBonusTab.confirmationTemplate.content')
              : t('order.orderBonusTab.confirmationTemplate.deleteContent')
          }
          yesActionTitle={
            deleteButton?.confirmationPopUp
              ? t('order.orderBonusTab.confirmationTemplate.delete')
              : t('detailsPage.confirmation.yes')
          }
          noActionTitle={
            deleteButton?.confirmationPopUp ? t('detailsPage.confirmation.cancel') : t('detailsPage.confirmation.no')
          }
          handleYesAction={createButton?.confirmationPopUp ? handleYesAction : handleDeleteAction}
          handleNoAction={createButton?.confirmationPopUp ? handleNoAction : handleDeleteNoAction}
          handleCancelAction={createButton?.confirmationPopUp ? handleCancelAction : null}
        />
      )}
      {(createButton?.clicked || editButton?.clicked) && (
        <OrderBonusModal
          open={createButton?.clicked || editButton?.clicked}
          title={
            createButton?.clicked
              ? t('order.orderBonusTab.modalTitles.addOrderBonus')
              : t('order.orderBonusTab.modalTitles.editOrderBonus')
          }
          rowData={
            createButton?.clicked && createButton?.selectedData && createButton?.useExistingTemplate
              ? createButton?.selectedData
              : editButton?.clicked && editButton?.selectedData
              ? editButton?.selectedData
              : null
          }
          orderId={orderId}
          disableControls={
            createButton?.clicked ||
            (editButton?.clicked && editButton?.selectedData?.descStatus === OrderBonusStatus.pending)
              ? false
              : true
          }
          isNewOrderBonus={createButton?.clicked}
          isEditAllowed={editButton?.clicked}
          useTemplate={createButton?.useExistingTemplate}
          onClose={() => {
            handleCancelAction();
          }}
        />
      )}
      {copyButton?.clicked && (
        <CopyOrderBonusModal
          open={copyButton?.clicked}
          title={t('order.orderBonusTab.modalTitles.copyOrderBonus')}
          sourceOrderId={orderId}
          bonusTemplateId={copyButton?.selectedBonusId}
          onClose={() => {
            setCopyButton({
              ...copyButton,
              clicked: false,
            });
          }}
        />
      )}
      {orderBonusChangeHistory?.open && <OrderBonusHistory />}
    </div>
  );
};
